/**
 * Hook wrapping the SSE chat client + the chat store.
 *
 * Caller provides the user's text; the hook handles store transitions
 * for the user/assistant message pair, tool-call inline rendering,
 * abort + error surfacing.
 */
import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { trackActivity } from '@/lib/analytics/tracker';
import { postChatTurn } from './api';
import { buildActiveContext } from './context';
import { usePortalChatStore } from './store';

export function useChatStream() {
  const location = useLocation();
  const abortRef = useRef<AbortController | null>(null);

  const {
    messages,
    appendUser,
    startAssistantTurn,
    appendAssistantToken,
    recordToolCall,
    recordToolResult,
    setInFlight,
    setError,
  } = usePortalChatStore();
  // Build the wire history without the per-message toolCalls field.
  const wireMessages = messages.map((m) => ({ role: m.role, content: m.content }));

  const send = useCallback(
    async (userText: string) => {
      const trimmed = userText.trim();
      if (!trimmed) return;
      const ctx = buildActiveContext(location.pathname);
      if (!ctx) {
        setError('Chat is only available on MARS pages.');
        return;
      }

      // Snapshot the conversation before we mutate the store — the SSE
      // call needs the full history at send time, not after we append
      // the new user message. Strip per-message toolCalls (wire shape).
      const history = [...wireMessages, { role: 'user' as const, content: trimmed }];

      appendUser(trimmed);
      startAssistantTurn();
      setError(null);
      setInFlight(true);

      const controller = new AbortController();
      abortRef.current = controller;

      // Audit summary fields collected during the stream — sent to the
      // shared user-activity tracker on turn end so the prompt shows
      // up in the admin/user-activity dashboard alongside page_views.
      let toolCount = 0;
      let usageIn = 0;
      let usageOut = 0;
      let outcome: 'ok' | 'error' = 'ok';

      try {
        await postChatTurn({
          messages: history,
          activeContext: ctx,
          signal: controller.signal,
          onEvent: (evt) => {
            switch (evt.type) {
              case 'token':
                appendAssistantToken(evt.text);
                break;
              case 'tool_call':
                toolCount++;
                recordToolCall({
                  id: evt.id, name: evt.name, input: evt.input, status: 'running',
                });
                break;
              case 'tool_result':
                recordToolResult(evt.id, evt.output);
                break;
              case 'error':
                outcome = 'error';
                setError(evt.message);
                break;
              case 'done':
                usageIn = evt.usage?.input_tokens ?? 0;
                usageOut = evt.usage?.output_tokens ?? 0;
                break;
            }
          },
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          outcome = 'error';
          setError((err as Error).message);
        }
      } finally {
        setInFlight(false);
        abortRef.current = null;
        // Per-turn entry in fed_data.user_activity — visible from the
        // admin user-activity dashboard. The full prompt + tool detail
        // + assistant response live in the server-side JSONL audit
        // log; this is the lightweight summary suitable for the table.
        trackActivity('chat_message', trimmed, {
          fund: ctx.fund,
          page: ctx.page,
          tool_count: toolCount,
          tokens_in: usageIn,
          tokens_out: usageOut,
          outcome,
        });
      }
    },
    [
      location.pathname, wireMessages,
      appendUser, startAssistantTurn, appendAssistantToken,
      recordToolCall, recordToolResult, setInFlight, setError,
    ],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { send, cancel };
}
